// Buscador de domicilios de AWS Place, compartido por el bloque de domicilio (pasos 02 y 10) y por
// la captura manual de la pantalla del comprobante. No busca ni guarda: recibe y avisa.
import {
    useEffect,
    useId,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Search } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { useAnchoredPanel } from '@/shared/hooks/useAnchoredPanel';
import { cn } from '@/lib/cn';
import type { AddressCandidate } from '../types';
import { InlineQueryError } from './QueryErrorNotice';

/**
 * Qué está pasando con la búsqueda, en una línea. Sin esto el vendedor teclea, no ve nada y asume
 * que está roto — o sigue escribiendo y dispara más llamadas.
 */
function SearchStatus({
    text,
    debounced,
    searching,
    failed,
    onRetry,
    results,
    listboxOpen,
}: {
    text: string;
    debounced: string;
    searching: boolean;
    failed: boolean;
    onRetry?: () => void;
    results: number;
    /** Con el panel abierto la cuenta la canta su encabezado; repetirla debajo es ruido. */
    listboxOpen: boolean;
}) {
    if (text.length === 0) return null;

    const noteClass = 'mt-1.5 text-[11.5px] text-muted-foreground';
    if (text.length < 3) {
        return <p className={noteClass}>Escribe al menos 3 caracteres para buscar.</p>;
    }
    // `text !== debounced` = lo tecleado todavía no llegó al buscador. Sin esto, entre la última
    // tecla y el disparo se ve un "sin resultados" que es mentira.
    if (searching || text !== debounced) return <p className={noteClass}>Buscando domicilios…</p>;
    // Va ANTES del "no encontramos nada": una búsqueda caída también llega con cero candidatos, y
    // decirle al vendedor que el domicilio no existe lo manda a teclear variantes del mismo.
    if (failed) {
        return (
            <InlineQueryError
                className="mt-1.5 text-[11.5px]"
                message="No se pudo buscar domicilios. Revisa la conexión."
                onRetry={onRetry}
            />
        );
    }
    if (results === 0) {
        return (
            <p className={noteClass}>
                No encontramos nada con eso. Prueba con la calle y el número, o con el código
                postal.
            </p>
        );
    }
    if (listboxOpen) return null;
    return (
        <p className={noteClass}>
            {results === 1 ? '1 domicilio encontrado' : `${String(results)} domicilios encontrados`}{' '}
            — toca el buscador para verlos.
        </p>
    );
}

/**
 * El candidato elegido, con qué hacer con él. Elegir no guarda: guardar es este botón, para que el
 * vendedor alcance a leer qué domicilio va a quedar en el expediente.
 */
export function AddressSaveConfirm({
    label,
    saving,
    onSave,
    onDiscard,
}: {
    label: string;
    saving: boolean;
    onSave: () => void;
    onDiscard: () => void;
}) {
    return (
        // Anunciado: el candidato se elige con el teclado y sin esto un lector de pantalla no dice
        // cuál quedó seleccionado ni que falta confirmar.
        <div role="status" className="mt-3 rounded-md border border-primary/40 bg-primary/5 p-3">
            <p className="text-[11.5px] text-muted-foreground">Vas a guardar este domicilio:</p>
            <p className="mt-1 break-words text-[12.5px] font-medium leading-relaxed">{label}</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button size="sm" className="w-full sm:w-auto" isLoading={saving} onClick={onSave}>
                    Guardar domicilio
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="w-full sm:w-auto"
                    disabled={saving}
                    onClick={onDiscard}
                >
                    Elegir otro
                </Button>
            </div>
        </div>
    );
}

export interface AddressComboboxProps {
    label: string;
    /** El buscador acaba de aparecer ⇒ el foco cae aquí, sin tabular por lo que está arriba. */
    focusOnMount?: boolean;
    query: string;
    onQueryChange: (value: string) => void;
    debouncedQuery: string;
    searching: boolean;
    candidates: AddressCandidate[];
    /** La consulta a AWS Place respondió con error: cero candidatos que NO significan "no existe". */
    searchFailed?: boolean;
    onRetrySearch?: () => void;
    chosen: AddressCandidate | null;
    onChoose: (candidate: AddressCandidate) => void;
}

/**
 * Combobox de domicilios: el panel de resultados va anclado al input, flotando encima de lo que
 * sigue, para que se vean varias opciones sin que la pantalla se estire ni haya que hacer scroll.
 */
export function AddressCombobox({
    label,
    focusOnMount = false,
    query,
    onQueryChange,
    debouncedQuery,
    searching,
    candidates,
    searchFailed = false,
    onRetrySearch,
    chosen,
    onChoose,
}: AddressComboboxProps) {
    const inputId = useId();
    const listboxId = useId();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    /** El vendedor cerró el panel (Escape o clic fuera) sin cambiar lo que tecleó. */
    const [dismissed, setDismissed] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    const results = candidates.length;
    const open = !dismissed && chosen === null && results > 0;
    // Si llegaron menos resultados que antes, el índice guardado ya no apunta a nada: se descarta al
    // leerlo en vez de arrastrar un resaltado fantasma.
    const active = highlight < results ? highlight : -1;
    const anchor = useAnchoredPanel(open, inputRef);

    // Por ref y no con el atributo `autoFocus` del DOM, que además roba el foco al rehidratar.
    useEffect(() => {
        if (focusOnMount) inputRef.current?.focus();
    }, [focusOnMount]);

    // Un clic fuera cierra el panel, pero deja intacto lo tecleado y lo elegido. El panel cuenta como
    // «dentro» aunque viva en `body`: sin esto, elegir con el mouse lo cerraría antes de seleccionar.
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event: PointerEvent): void => {
            const target = event.target as Node;
            const inside =
                rootRef.current?.contains(target) === true ||
                panelRef.current?.contains(target) === true;
            if (!inside) setDismissed(true);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [open]);

    // Navegar con flechas tiene que arrastrar el scroll del panel, o el resaltado se pierde abajo.
    useEffect(() => {
        if (active < 0) return;
        optionRefs.current[active]?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
        const { key } = event;

        if (key === 'Escape') {
            if (!open) return;
            event.preventDefault();
            setDismissed(true);
            setHighlight(-1);
            return;
        }
        if (key === 'ArrowDown' || key === 'ArrowUp') {
            if (results === 0) return;
            event.preventDefault();
            // Con el panel cerrado la primera flecha lo reabre desde el principio de la lista.
            const from = open ? active : -1;
            const next = key === 'ArrowDown' ? from + 1 : from - 1;
            setDismissed(false);
            setHighlight(next < 0 ? results - 1 : next % results);
            return;
        }
        if (key === 'Enter' && open && active >= 0) {
            event.preventDefault();
            onChoose(candidates[active]);
            return;
        }
        if (key === 'Tab' && open) setDismissed(true);
    };

    return (
        <div ref={rootRef} className="relative">
            <label className="mb-1 block text-[11.5px] text-muted-foreground" htmlFor={inputId}>
                {label}
            </label>

            <div className="relative">
                <Search
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                />
                <input
                    id={inputId}
                    ref={inputRef}
                    role="combobox"
                    aria-expanded={open}
                    // Cerrado el panel no existe: apuntar a un id inexistente es una referencia rota.
                    aria-controls={open ? listboxId : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        open && active >= 0 ? `${listboxId}-${String(active)}` : undefined
                    }
                    className="w-full rounded-md border border-border bg-card py-2 pl-9 pr-9 text-[12.5px] focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Calle y número, colonia o código postal"
                    autoComplete="off"
                    value={query}
                    onKeyDown={onKeyDown}
                    onChange={(e) => {
                        setDismissed(false);
                        setHighlight(-1);
                        onQueryChange(e.target.value);
                    }}
                />
                {searching && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loading size="sm" />
                    </span>
                )}
            </div>

            {/* Portaleado a `body` y con posición fija: dentro de la card lo recortaría su
                `overflow-hidden`, y así lo que sigue tampoco se empuja al llegar resultados. */}
            {open &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{
                            position: 'fixed',
                            left: anchor?.left ?? 0,
                            width: anchor?.width ?? 0,
                            top: anchor?.top,
                            bottom: anchor?.bottom,
                            maxHeight: anchor?.maxHeight,
                        }}
                        className="z-50 flex flex-col overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
                    >
                        <p className="flex-none border-b border-border bg-muted/60 px-3 py-1.5 text-[11px] text-muted-foreground">
                            {results === 1
                                ? '1 domicilio encontrado'
                                : `${String(results)} domicilios encontrados`}{' '}
                            — elige en cuál vive el cliente.
                        </p>
                        <ul
                            id={listboxId}
                            role="listbox"
                            aria-label="Domicilios encontrados"
                            className="max-h-[17.5rem] min-h-0 overflow-y-auto py-1"
                        >
                            {candidates.map((c, i) => {
                                const highlighted = i === active;
                                return (
                                    // El teclado vive en el input (patrón aria-activedescendant):
                                    // las opciones no llevan handlers ni foco propios.
                                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                                    <li
                                        key={c.etiqueta}
                                        id={`${listboxId}-${String(i)}`}
                                        ref={(el) => {
                                            optionRefs.current[i] = el;
                                        }}
                                        role="option"
                                        aria-selected={highlighted}
                                        className={cn(
                                            'flex cursor-pointer items-start gap-2.5 px-3 py-2 text-left transition-colors',
                                            highlighted ? 'bg-primary/10' : 'hover:bg-muted',
                                        )}
                                        // Sin esto el clic le roba el foco al input y el panel se
                                        // cierra antes de que la opción se elija.
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                        }}
                                        onMouseEnter={() => {
                                            setHighlight(i);
                                        }}
                                        onClick={() => {
                                            onChoose(c);
                                        }}
                                    >
                                        <MapPin
                                            className={cn(
                                                'mt-0.5 size-4 shrink-0',
                                                highlighted
                                                    ? 'text-primary'
                                                    : 'text-muted-foreground',
                                            )}
                                            aria-hidden
                                        />
                                        <span className="min-w-0 break-words text-[12px] leading-relaxed">
                                            {c.etiqueta}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>,
                    document.body,
                )}

            {/* El buscador tarda, así que decir en qué estado está evita que el vendedor
                crea que no funciona y siga tecleando. */}
            <SearchStatus
                text={query.trim()}
                debounced={debouncedQuery}
                searching={searching}
                failed={searchFailed}
                onRetry={onRetrySearch}
                results={results}
                listboxOpen={open}
            />
        </div>
    );
}
